/**
 * LiveKitMultiViewer — VIEWER-side grid for linked (multi-broadcaster) streams.
 * LiveKit replacement for AgoraMultiplayerViewer. Subscriber-only: never
 * publishes. Renders 1–16 broadcaster tiles (Round 63: a Party) with correct aspect handling:
 * a tile measures the real video dimensions and, when the video orientation
 * doesn't match the tile, letterboxes over a blurred fill (NO zoom-crop).
 *
 *   <LiveKitMultiViewer
 *     token={token} serverUrl={LIVEKIT_URL} roomName={room}
 *     hostInfo={{ user_id, name, picture }} linkedPartners={[{id,name,picture}]}
 *     onError={(e)=>...} onDisconnected={()=>...}
 *   />
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Room, RoomEvent, Track, VideoQuality } from 'livekit-client';

function SmartVideoTile({ track, label, picture, muted }) {
  const videoRef = useRef(null);
  const bgRef = useRef(null);
  const [fit, setFit] = useState('cover');

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !track) return;
    track.attach(el);
    if (bgRef.current) track.attach(bgRef.current);

    const decideFit = () => {
      const vw = el.videoWidth, vh = el.videoHeight;
      if (!vw || !vh) return;
      const tileRect = el.parentElement?.getBoundingClientRect();
      if (!tileRect || !tileRect.width || !tileRect.height) return;
      const videoPortrait = vh > vw;
      const tilePortrait = tileRect.height > tileRect.width;
      // Same orientation → fill the tile. Mismatch → contain (letterbox) so we
      // never zoom-crop someone's camera; blurred bg fills the empty bands.
      setFit(videoPortrait === tilePortrait ? 'cover' : 'contain');
    };
    el.addEventListener('loadedmetadata', decideFit);
    el.addEventListener('resize', decideFit);
    decideFit();
    return () => {
      el.removeEventListener('loadedmetadata', decideFit);
      el.removeEventListener('resize', decideFit);
      try { track.detach(el); } catch {}
      if (bgRef.current) { try { track.detach(bgRef.current); } catch {} }
    };
  }, [track]);

  return (
    <div className="relative w-full h-full overflow-hidden bg-black">
      {/* blurred fill behind letterboxed video */}
      {fit === 'contain' && (
        <video
          ref={bgRef}
          autoPlay playsInline muted
          className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-50"
        />
      )}
      <video
        ref={videoRef}
        autoPlay playsInline muted={muted}
        className="absolute inset-0 w-full h-full"
        style={{ objectFit: fit }}
      />
      <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1.5 bg-black/55 rounded-full pl-1 pr-2.5 py-0.5 backdrop-blur-sm">
        {picture ? (
          <img src={picture} alt="" className="w-5 h-5 rounded-full object-cover" />
        ) : (
          <div className="w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center text-[10px] text-white font-bold">
            {(label || '?')[0]}
          </div>
        )}
        <span className="text-white text-[11px] font-semibold truncate max-w-[90px]">{label}</span>
      </div>
    </div>
  );
}

// Round 63: every face fully visible, up to SIXTEEN broadcasters (a Party).
function partyGridClass(total) {
  if (total <= 4) return 'grid-cols-2 grid-rows-2';
  if (total <= 6) return 'grid-cols-3 grid-rows-2';
  if (total <= 9) return 'grid-cols-3 grid-rows-3';
  if (total <= 12) return 'grid-cols-4 grid-rows-3';
  return 'grid-cols-4 grid-rows-4';
}

export default function LiveKitMultiViewer({
  token, serverUrl, roomName, hostInfo, linkedPartners = [],
  onError, onDisconnected, onParticipantCount,
}) {
  const roomRef = useRef(null);
  const [tiles, setTiles] = useState([]); // [{ identity, track, audioTrack }]
  const [connected, setConnected] = useState(false);

  const nameFor = useCallback((identity) => {
    if (hostInfo && identity === hostInfo.user_id) return hostInfo.name;
    const p = (linkedPartners || []).find((x) => x.id === identity);
    return p?.name || 'Guest';
  }, [hostInfo, linkedPartners]);

  const picFor = useCallback((identity) => {
    if (hostInfo && identity === hostInfo.user_id) return hostInfo.picture;
    return (linkedPartners || []).find((x) => x.id === identity)?.picture;
  }, [hostInfo, linkedPartners]);

  useEffect(() => {
    if (!token || !serverUrl) return;
    let cancelled = false;

    const room = new Room({
      // pixelDensity 'screen' → phones request the high simulcast layer
      // instead of a CSS-pixel-sized (soft) one.
      adaptiveStream: { pixelDensity: 'screen' },
    });
    roomRef.current = room;

    const rebuild = () => {
      if (cancelled) return;
      const next = [];
      room.remoteParticipants.forEach((p) => {
        let video = null;
        p.videoTrackPublications.forEach((pub) => {
          if (pub.track && pub.source !== Track.Source.ScreenShareAudio) video = pub.track;
          if (pub.setVideoQuality) { try { pub.setVideoQuality(VideoQuality.HIGH); } catch {} }
        });
        p.audioTrackPublications.forEach((pub) => {
          if (pub.track) {
            try { pub.track.attach(); } catch {} // audio plays via hidden element
          }
        });
        if (video) next.push({ identity: p.identity, track: video });
      });
      // Stable order: host first, then partners by name.
      next.sort((a, b) => {
        const aHost = hostInfo && a.identity === hostInfo.user_id ? 0 : 1;
        const bHost = hostInfo && b.identity === hostInfo.user_id ? 0 : 1;
        return aHost - bHost || nameFor(a.identity).localeCompare(nameFor(b.identity));
      });
      setTiles(next);
      if (onParticipantCount) onParticipantCount(next.length);
    };

    room
      .on(RoomEvent.TrackSubscribed, rebuild)
      .on(RoomEvent.TrackUnsubscribed, rebuild)
      .on(RoomEvent.ParticipantConnected, rebuild)
      .on(RoomEvent.ParticipantDisconnected, rebuild)
      .on(RoomEvent.Disconnected, () => { if (!cancelled && onDisconnected) onDisconnected(); });

    room.connect(serverUrl, token)
      .then(() => { if (!cancelled) { setConnected(true); rebuild(); } })
      .catch((e) => { if (!cancelled && onError) onError(e); });

    return () => {
      cancelled = true;
      try { room.disconnect(); } catch {}
      roomRef.current = null;
    };
  }, [token, serverUrl, roomName]);

  // Layout: 1 → full; 2 → side-by-side, evenly split (never stacked on
  // mobile); 3 → 2×2 with 3rd tile spanning both cols of row 2; 4 → 2×2.
  let gridClass;
  let extraChildClass = '';
  if (tiles.length <= 1) {
    gridClass = 'grid-cols-1 grid-rows-1';
  } else if (tiles.length === 2) {
    gridClass = 'grid-cols-2 grid-rows-1';
  } else if (tiles.length === 3) {
    gridClass = 'grid-cols-2 grid-rows-2';
    extraChildClass = '[&>*:nth-child(3)]:col-span-2';
  } else {
    gridClass = partyGridClass(tiles.length);   // Round 63: 4 → 2×2 … 16 → 4×4
  }

  return (
    <div className="w-full h-full bg-black" data-testid="livekit-multi-viewer">
      {!connected || tiles.length === 0 ? (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-400 mx-auto mb-3" />
            <p className="text-sm text-gray-400">Connecting to broadcasters…</p>
          </div>
        </div>
      ) : (
        <div className={`grid gap-0.5 w-full h-full ${gridClass} ${extraChildClass}`}>
          {tiles.map((t) => (
            <SmartVideoTile
              key={t.identity}
              track={t.track}
              label={nameFor(t.identity)}
              picture={picFor(t.identity)}
              muted={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}
